import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Row, Col, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button as CrcButton } from '@crc-evans/ui-components';
import { useArcWeldingFormOptions } from '../../hooks/useArcWeldingFormOptions';
import { AwFormSelect } from './ArcWeldingListShell';
import {
  AW_FORM_LABELS,
  AW_MODAL_SECTIONS,
  AW_VALUE_CLASSIFICATIONS,
} from '../../utils/arcWeldingVerificationUi';
import './ArcWelding.css';

const ArcWeldingEquipmentProfileForm = ({ open, initialValues, onCancel, onSubmit, loading }) => {
  const [form] = Form.useForm();
  const { options } = useArcWeldingFormOptions();

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        isActive: true,
        ...initialValues,
      });
    } else {
      form.resetFields();
    }
  }, [open, initialValues, form]);

  if (!open) return null;

  const handleSave = async () => {
    const values = await form.validateFields();
    onSubmit(values);
  };

  const valueClassifications = options.valueClassification?.length
    ? options.valueClassification
    : AW_VALUE_CLASSIFICATIONS;

  const pageTitle = initialValues?.id ? 'Edit Equipment Profile' : 'New Equipment Profile';
  const subtitle = initialValues?.profileName || 'Data Rev 1 register';

  return (
    <Spin spinning={Boolean(loading)}>
      <div className="aw-certificate-form aw-admin-form-page heat-profile-content-wrapper">
        <div className="aw-form-page-header">
          <div className="aw-form-page-header-top">
            <div className="aw-form-page-title-group">
              <h1 className="aw-form-page-title">{pageTitle}</h1>
              <p className="aw-form-page-subtitle">{subtitle}</p>
            </div>
          </div>
          <div className="aw-form-page-header-bottom">
            <button
              type="button"
              className="heat-profile-page-back-link aw-form-back-btn"
              onClick={onCancel}
            >
              <span className="heat-profile-back-link">
                <ArrowLeftOutlined /> Back to List
              </span>
            </button>
            <div className="heat-profile-header-actions calibration-modern-actions aw-form-header-actions">
              <CrcButton label="Cancel" variant="tertiary" size="small" onClick={onCancel} />
              <CrcButton
                label={loading ? 'Saving…' : 'Save'}
                variant="primary"
                size="small"
                onClick={handleSave}
                disabled={loading}
              />
            </div>
          </div>
        </div>

        <Form form={form} layout="vertical" className="aw-admin-form-fields">
          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.PROFILE_DETAILS}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} md={12}>
                <Form.Item name="profileName" label={AW_FORM_LABELS.profileName} rules={[{ required: true }]}>
                  <Input placeholder="Enter Profile Name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="equipmentType" label={AW_FORM_LABELS.equipmentType} rules={[{ required: true }]}>
                  <AwFormSelect
                    options={options.equipmentType}
                    placeholder="Select Equipment Type"
                    showSearch
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="manufacturer" label={AW_FORM_LABELS.manufacturer}>
                  <Input placeholder="Enter Manufacturer" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="model" label={AW_FORM_LABELS.model}>
                  <Input placeholder="Enter Model" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.RATING_PLATE_RANGES}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={8}>
                <Form.Item name="currentMinA" label={AW_FORM_LABELS.currentMinA}>
                  <InputNumber className="aw-full-width-input" placeholder="Min" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="currentMaxA" label={AW_FORM_LABELS.currentMaxA}>
                  <InputNumber className="aw-full-width-input" placeholder="Max" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="ratedNoLoadU0V" label={AW_FORM_LABELS.ratedNoLoadU0V}>
                  <InputNumber className="aw-full-width-input" placeholder="U0" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="voltageMinV" label={AW_FORM_LABELS.voltageMinV}>
                  <InputNumber className="aw-full-width-input" placeholder="Min" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="voltageMaxV" label={AW_FORM_LABELS.voltageMaxV}>
                  <InputNumber className="aw-full-width-input" placeholder="Max" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="wfsMinMPerMin" label={AW_FORM_LABELS.wfsMin}>
                  <InputNumber className="aw-full-width-input" placeholder="Min" step={0.1} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="wfsMaxMPerMin" label={AW_FORM_LABELS.wfsMax}>
                  <InputNumber className="aw-full-width-input" placeholder="Max" step={0.1} />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="aw-section aw-section--form">
            <div className="aw-section-title">{AW_MODAL_SECTIONS.CERTIFICATE_DEFAULTS}</div>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="defaultGrade" label={AW_FORM_LABELS.defaultGrade}>
                  <AwFormSelect options={options.grade} placeholder="Select Grade" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="defaultProcess" label={AW_FORM_LABELS.defaultProcess}>
                  <AwFormSelect
                    options={options.process}
                    placeholder="Select Process"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Form.Item name="defaultOutputMode" label={AW_FORM_LABELS.defaultOutputMode}>
                  <AwFormSelect
                    options={options.outputMode}
                    placeholder="Select Output Mode"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="defaultVerificationRange" label={AW_FORM_LABELS.defaultVerificationRange}>
                  <AwFormSelect
                    options={options.verificationRange}
                    placeholder="Select Verification Range"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="defaultValueClassification" label={AW_FORM_LABELS.defaultValueClassification}>
                  <AwFormSelect
                    options={valueClassifications}
                    placeholder="Select Classification"
                    allowClear
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="defaultFrequency" label={AW_FORM_LABELS.defaultFrequency}>
                  <Input placeholder="e.g. 50 Hz" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="isActive"
                  label={AW_FORM_LABELS.active}
                  valuePropName="checked"
                  className="aw-form-switch-item"
                >
                  <Switch
                    className="aw-active-switch"
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item name="registerNote" label={AW_FORM_LABELS.registerNote}>
                  <Input.TextArea rows={3} placeholder="Optional Register Note" />
                </Form.Item>
              </Col>
            </Row>
          </div>
        </Form>
      </div>
    </Spin>
  );
};

export default ArcWeldingEquipmentProfileForm;
